// ============================================================================
// Project Work — list page. Default scope is "mine" (current AM's projects);
// has_full_am_authority roles can flip to "all" to see every active project.
// Status filter mirrors the DB CHECK on projects.status.
//
// 2026-05-28 — AFB-0005: Sort dropdown with 5 options. Default is
// "Newest first" (created_at DESC). Persisted in localStorage so the user's
// choice survives reloads.
// ============================================================================

import { hasFinanceCapability, startsOnReadyToInvoice } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { ProjectCard } from './project-card';
import { canReadPaymentRecords } from './lib/line-owned-payment';
import { projectDivisionLabel, projectServiceLabel } from './lib/project-service-label';
import { useProjects } from './hooks/use-project-data';
import { useAwaitingAmToast } from './hooks/use-awaiting-am-toast';
import { useProjectAttribution } from './hooks/use-project-attribution';
import type { ProjectSort } from './types';
import { useWorkScope, WorkScopeChips } from '../../shared/scope';
import { PROJECT_SORT_OPTIONS } from './types';
import {
  PROJECT_STAGE_LABELS,
  type ProjectStage,
  type ProjectRow,
} from '../../shared/types';

// 2026-08-20: the Status chip row is gone with projects.status. It defaulted to
// ['Active'] — which meant "created in the app", not "still live" — and so
// silently hid 340 of the 354 rows in the Archive view ($243,509.77 of
// billings). The stage views below already split live work from history on the
// axis that actually means something.

// F2 (2026-06-02) — the stage filter is presented as "views" rather than raw
// DB stages. The Invoiced view is split on a 60-day rolling boundary: it shows
// only recently-invoiced projects, while anything invoiced more than 60 days
// ago falls into the Archive view alongside explicitly-Completed projects.
// This is a pure client-side view filter on invoiced_at — no cron, no stage
// mutation. Once a project is invoiced, AR/aging tracking lives in QBO, so the
// active Invoiced lane doesn't need to accumulate forever.
const INVOICED_ACTIVE_DAYS = 60;
type StageView = ProjectStage | 'Archive';
const STAGE_VIEW_OPTIONS: StageView[] = [
  // ⛔ ONE "Not sent" chip, not two — 2026-09-03. `Draft` and `Awaiting_AM_Input`
  // now render the same word, so offering both filters put the same label on
  // screen twice. The Draft chip fetches BOTH stages (see viewsToStages), so a
  // bounced-back card is still reachable and is not stranded behind a chip that
  // no longer exists.
  'Draft',
  'Ready_to_Invoice',
  'Pending_PO',
  'Invoiced',
  'Archive',
];
// Archive replaces the standalone Completed chip — it holds Completed projects
// plus anything invoiced more than INVOICED_ACTIVE_DAYS ago.
const STAGE_VIEW_LABELS: Record<StageView, string> = {
  ...PROJECT_STAGE_LABELS,
  Archive: 'Archive',
};
// Map the selected views to the DB stages the server query must fetch. Archive
// pulls Completed + Invoiced; the 60-day split is then applied client-side.
function viewsToStages(views: StageView[]): ProjectStage[] {
  const set = new Set<ProjectStage>();
  for (const v of views) {
    if (v === 'Archive') {
      set.add('Completed');
      set.add('Invoiced');
    } else if (v === 'Draft') {
      // One chip, both stages. They are the same thing to the AM.
      set.add('Draft');
      set.add('Awaiting_AM_Input');
    } else {
      set.add(v);
    }
  }
  return [...set];
}

// An Invoiced project is "stale" (archived) once it was invoiced more than
// INVOICED_ACTIVE_DAYS ago. A null invoiced_at keeps it in the active Invoiced
// view so finance never loses sight of an un-stamped invoiced row.
function isStaleInvoiced(row: ProjectRow, cutoffMs: number): boolean {
  if (row.stage !== 'Invoiced' || !row.invoiced_at) return false;
  return new Date(row.invoiced_at).getTime() < cutoffMs;
}

// Whether a fetched row belongs in the currently-selected views, applying the
// Invoiced/Archive 60-day split. Other stages match their view 1:1.
function rowInViews(row: ProjectRow, views: StageView[], cutoffMs: number): boolean {
  if (row.stage === 'Invoiced') {
    return isStaleInvoiced(row, cutoffMs)
      ? views.includes('Archive')
      : views.includes('Invoiced');
  }
  if (row.stage === 'Completed') return views.includes('Archive');
  // The "Not sent" chip covers both stages that render as Not sent.
  if (row.stage === 'Awaiting_AM_Input' && views.includes('Draft')) return true;
  return views.includes(row.stage as StageView);
}

const LS_SORT_KEY = 'tcs:project-work:sort';
const VALID_SORTS: ProjectSort[] = PROJECT_SORT_OPTIONS.map((o) => o.value);

function loadSortFromStorage(defaultSort: ProjectSort): ProjectSort {
  try {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(LS_SORT_KEY) : null;
    if (v && (VALID_SORTS as string[]).includes(v)) return v as ProjectSort;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return defaultSort;
}

// Free-text search haystack for a project row. Concatenates the fields an AM
// or finance would search by — facility, account, AM, submitter, sub/partner,
// type of work — plus id / category / status / stage / notes. All client-side:
// the list already fetches every matching row with these joins hydrated, so we
// filter in memory (no new query, RPC, or schema change).
function buildProjectSearchText(row: ProjectRow): string {
  const am = row.facilities?.account_manager;
  const c = row.creator;
  return [
    row.project_id,
    row.facility_name,
    row.facilities?.facility_name,
    row.account_name,
    row.accounts?.account_name,
    row.facilities?.market_region,
    projectServiceLabel(row),
    projectDivisionLabel(row),
    row.stage,
    row.notes,
    row.partner?.provider_name,
    am ? `${am.employee_name_first ?? ''} ${am.employee_name_last ?? ''}` : '',
    c ? `${c.employee_name_first ?? ''} ${c.employee_name_last ?? ''}` : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

// item #650 — which cards are shown is the work view (My facilities / My markets /
// All TCS), the same switch as Home and My Work, and where a person starts comes
// from the database (get_work_scopes): account managers on their own, sales on
// their markets, executives, finance and HR on everything. "Mine" is a card I
// created or a card on one of my buildings (Ben, 2026-09-28, D4).
// Roles whose default stage filter is the billing-ready queue rather than the
// AM working queue. Mirrors isHighAuthority today but kept separate so we can
// diverge later (e.g. if executives want a different default stage).
function isFinanceLike(role: string | null | undefined): boolean {
  // 2026-05-28: HR added — Antonela helps Jose work the Ready-to-Invoice queue.
  return startsOnReadyToInvoice(role);
}

export default function ProjectList() {
  const { employee, profile } = useAuth();
  // OR-in the finance capability so extra_roles 'finance' hat holders (e.g.
  // Antonela) get the cross-AM 'all' scope + Ready-to-Invoice default, the same
  // as primary finance. Superset of the existing role lists — no regression.
  const financeLike = isFinanceLike(profile?.role) || hasFinanceCapability(profile);
  // Item #680 — whether payment records are readable at all (lib/line-owned-payment.ts).
  const canSeePayments = canReadPaymentRecords(profile?.role);

  // AFB-0009 — surface bounced-back cards to the AM on entering Project Work.
  useAwaitingAmToast(employee?.employee_id ?? null, profile?.last_login_at ?? null);

  const ws = useWorkScope();
  // Default stage view: finance/exec/superuser/national_am see Ready to Invoice;
  // AM-style roles default to their working queue (Draft).
  const [views, setViews] = useState<StageView[]>(
    financeLike ? ['Ready_to_Invoice'] : ['Draft'],
  );
  // F12 — finance defaults to oldest-first (their billing queues are worked
  // oldest-unbilled-first); everyone else defaults to newest-first. A stored
  // preference always wins.
  const [sort, setSort] = useState<ProjectSort>(() =>
    loadSortFromStorage(financeLike ? 'created_asc' : 'created_desc'),
  );
  const [search, setSearch] = useState('');

  // Persist sort selection — survives reloads even if the user doesn't refresh
  // any data first.
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_SORT_KEY, sort);
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, [sort]);

  const filter = useMemo(
    () => ({
      scope: !ws.ready
        ? { facilityIds: [] as string[] }
        : ws.scope === 'all'
          ? null
          : { facilityIds: ws.facilityIds ?? [], orCreatedBy: ws.scope === 'mine' ? (employee?.employee_id ?? null) : null },
      stages: views.length > 0 ? viewsToStages(views) : undefined,
      sort,
    }),
    [ws.ready, ws.scope, ws.facilityIds, employee?.employee_id, views, sort],
  );

  const { data, isLoading, error, refetch } = useProjects(filter);

  // AFB — client-side free-text search over the already-fetched rows. Space-
  // separated terms are AND-matched (e.g. "byron mary" needs both), so the
  // user can narrow by combining a sub + facility + AM in one box.
  const visible = useMemo(() => {
    const cutoffMs = Date.now() - INVOICED_ACTIVE_DAYS * 24 * 60 * 60 * 1000;
    const q = search.trim().toLowerCase();
    const terms = q ? q.split(/\s+/) : [];
    return data.filter((row) => {
      // F2 — enforce the Invoiced/Archive 60-day split client-side. The server
      // already scoped the fetch to viewsToStages(views); this hides stale
      // invoiced rows from the active Invoiced view (and vice-versa).
      if (views.length > 0 && !rowInViews(row, views, cutoffMs)) return false;
      if (terms.length === 0) return true;
      const hay = buildProjectSearchText(row);
      return terms.every((t) => hay.includes(t));
    });
  }, [data, search, views]);

  // 2026-09-02 — attribution for the cards actually on screen: TWO queries for
  // the whole board, never one per card (the fetchRateBasisMap pattern). The id
  // list is what the hook keys on, so filtering or searching refetches and
  // scrolling does not.
  const attribution = useProjectAttribution(
    useMemo(() => visible.map((r) => r.project_id).filter((id): id is string => !!id), [visible]),
  );

  function toggleView(s: StageView) {
    setViews((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <PageHeader
        title="Projects & Billing Comms"
        meta="Project work, and everything else you need to tell Finance about money."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/project-work/queue"
              className="inline-flex min-h-[44px] items-center text-sm text-tcs-text-secondary hover:text-tcs-text"
            >
              Classification queue
            </Link>
            <Link
              to="/project-work/findings"
              className="inline-flex min-h-[44px] items-center text-sm text-tcs-text-secondary hover:text-tcs-text"
            >
              Findings
            </Link>
            <Link to="/project-work/new">
              <Button variant="primary" size="sm" className="min-h-[44px]">
                <Plus className="h-4 w-4" /> New project
              </Button>
            </Link>
          </div>
        }
      />

      <div className="mb-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search facility, AM, submitter, sub, type of work…"
            className="h-9 w-full rounded-md border border-tcs-border bg-tcs-bg pl-8 pr-3 text-sm text-tcs-text placeholder:text-tcs-text-muted focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <WorkScopeChips
          scope={ws.scope}
          options={ws.options}
          onChange={ws.setScope}
          labelFor={(o) => (o.scope === 'mine' ? 'Mine' : o.label)}
        />
        {/* AFB-0005 — sort dropdown. Inline with existing toolbar controls. */}
        <div className="ml-auto flex items-center gap-1">
          <label className="text-[11px] uppercase tracking-wide text-tcs-text-muted" htmlFor="project-work-sort">
            Sort:
          </label>
          <select
            id="project-work-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as ProjectSort)}
            className="h-7 rounded-md border border-tcs-border bg-tcs-bg px-2 text-xs font-medium text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          >
            {PROJECT_SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1 text-sm">
        <span className="mr-1 text-[11px] uppercase tracking-wide text-tcs-text-muted">Stage:</span>
        {STAGE_VIEW_OPTIONS.map((s) => {
          const active = views.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleView(s)}
              className={`inline-flex min-h-[44px] items-center rounded-md border px-3 text-xs font-medium ${
                active
                  ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-inverse/40'
              }`}
            >
              {STAGE_VIEW_LABELS[s]}
            </button>
          );
        })}
      </div>

      {error && (
        <ErrorState
          title="Couldn't load projects"
          body={error}
          onRetry={refetch}
        />
      )}

      {isLoading && !error && (
        <ul className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i}>
              <Skeleton className="h-20 w-full rounded-md" />
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && visible.length === 0 && (
        <EmptyState
          title={search.trim() ? 'No matches' : 'No projects'}
          body={
            search.trim()
              ? `No projects match “${search.trim()}”. Try a facility, AM, submitter, sub, or service type.`
              : ws.scope === 'mine'
                ? "You haven't created any projects yet. Use “New project” to start one."
                : 'No projects match the current filters.'
          }
        />
      )}

      {!isLoading && !error && visible.length > 0 && (
        <ul className="space-y-2">
          {visible.map((row) => (
            <ProjectCard
              key={row.project_id}
              row={row}
              attribution={attribution.rows.get(row.project_id) ?? null}
              nameFor={attribution.nameFor}
              canSeePayments={canSeePayments}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
